import isPlainObject from 'lodash/isPlainObject';
import { getRecoil, setRecoil } from 'recoil-nexus';
import { v4 } from 'uuid';

import { iaRa } from 'lib/helpers/utility';

import {
  activePromptConversationsState,
  intelligenceChatIdState,
  intelligenceChatMessageIdState,
  isAIGeneratingState,
  myScribeAILoadingState,
  myScribeAutoMapActivePromptIdState,
  myScribeAutoMapSelectedNoteTypeState,
  promptListState
} from 'components/practice/charts/ClinicalNote/MyScribeAI/state';
import { intelligenceAiState } from 'components/state';

import { updatePromptList } from './helpers';

let isGenerationStarted = false;
const chunkBuffers = {};
const updateAutoMapTemplate = (data) => {
  const {
    vertical,
    response,
    done = false,
    abort = false,
    isRegenerate = false,
    isDescription = false,
    isNote = true,
    note,
    ...rest
  } = data;
  const isResponseObject = isPlainObject(response);

  const activePromptConversations = getRecoil(activePromptConversationsState);
  const isFollowUp = activePromptConversations?.length > 2;

  try {
    if (isDescription) {
      setRecoil(promptListState, (prevPrompts = []) =>
        updatePromptList(
          prevPrompts,
          rest?.intelligence_chat_id,
          rest?.intelligenceChat,
          isDescription
        )
      );
      return;
    }

    const intelligenceChatId = getRecoil(intelligenceChatIdState);
    if (
      rest?.intelligence_chat_id &&
      intelligenceChatId &&
      rest?.intelligence_chat_id !== intelligenceChatId
    ) {
      return;
    }

    // Handle initial generation (first request, not regenerate, not follow-up)
    if (!isGenerationStarted && !isRegenerate && !isFollowUp) {
      setRecoil(promptListState, (prevPrompts = []) =>
        updatePromptList(prevPrompts, rest?.intelligence_chat_id, rest?.intelligenceChat)
      );
      setRecoil(myScribeAutoMapActivePromptIdState, rest?.intelligence_chat_id);
      setRecoil(isAIGeneratingState, true);
      setRecoil(myScribeAILoadingState, true);
      isGenerationStarted = true;
      return;
    }

    // Handle regenerate case
    if (isRegenerate && !done && !abort && !isGenerationStarted) {
      setRecoil(isAIGeneratingState, true);
      setRecoil(myScribeAILoadingState, true);
      isGenerationStarted = true;
    }

    if (done || abort) {
      setRecoil(
        myScribeAutoMapActivePromptIdState,
        abort ? rest?.intelligenceChat?.id : rest?.intelligence_chat_id
      );
      setRecoil(myScribeAILoadingState, false);
      if (!isResponseObject) {
        setRecoil(isAIGeneratingState, false);
      }

      isGenerationStarted = false;
    }
    if (!isNote) {
      setRecoil(myScribeAILoadingState, false);
    }

    if (rest?.intelligence_chat_id) {
      setRecoil(intelligenceChatIdState, rest?.intelligence_chat_id);
    }
    const newAiClinicalNoteState = getRecoil(intelligenceAiState);
    if (newAiClinicalNoteState?.timeoutTimeout)
      clearTimeout(newAiClinicalNoteState?.timeoutTimeout);

    setPromptStates({
      done,
      abort,
      response,
      path: vertical,
      isRegenerate,
      isNote,
      isFollowUp,
      note: isNote ? note : null,
      ...rest
    });
  } catch (error) {
    console.error('❌ Error', error);
  }
};

const setPromptStates = (intelligenceAi) => {
  if (intelligenceAi?.response) {
    if (!intelligenceAi?.isRegenerate) {
      setRecoil(myScribeAutoMapActivePromptIdState, intelligenceAi?.intelligence_chat_id);
    }
    if (intelligenceAi?.done || intelligenceAi?.abort) {
      setRecoil(myScribeAutoMapSelectedNoteTypeState, intelligenceAi.noteType);
      if (intelligenceAi?.intelligenceChat) {
        const { id, name, description, noteType } = intelligenceAi?.intelligenceChat || {};
        setRecoil(promptListState, (prevPrompts) => {
          if (prevPrompts?.[0]?.id === id && !intelligenceAi?.isFollowUp) {
            const newPrompts = [...prevPrompts];
            newPrompts[0] = {
              id,
              name,
              description,
              noteType
            };
            return newPrompts;
          }
          return prevPrompts;
        });
      }
      if (intelligenceAi?.intelligenceChatMessage) {
        setRecoil(intelligenceChatMessageIdState, intelligenceAi?.intelligenceChatMessage?.id);
        setRecoil(activePromptConversationsState, (prevState) => {
          let newPrompt = [...iaRa(prevState)];
          if (newPrompt[newPrompt?.length - 1]?.agent === 'ai') {
            newPrompt[newPrompt?.length - 1] = {
              ...intelligenceAi?.intelligenceChatMessage,
              emCPTs: intelligenceAi?.emCPTs,
              note: intelligenceAi?.note
            };
          } else {
            newPrompt = [
              ...iaRa(prevState),
              {
                ...intelligenceAi?.intelligenceChatMessage,
                emCPTsText: intelligenceAi?.emCPTsText,
                note: intelligenceAi?.note
              }
            ];
          }
          return newPrompt;
        });
      }
    }
    if (!intelligenceAi?.isNote || intelligenceAi?.done) {
      const chatId = intelligenceAi?.intelligence_chat_id;
      const orderId = intelligenceAi?.response.orderId;
      const value = intelligenceAi?.response.value;
      const isResponseObject = isPlainObject(intelligenceAi?.response);
      const isLast = intelligenceAi?.done && !isResponseObject;
      if (!chunkBuffers[chatId]) {
        chunkBuffers[chatId] = { chunks: {}, lastIndex: null, latestCompleteMessage: '' };
      }
      const buffer = chunkBuffers[chatId];
      buffer.chunks[orderId] = value;
      if (isLast) {
        buffer.lastIndex = orderId;
      }
      // Assemble continuous chunks starting from 0
      const assembled = [];
      let i = 0;
      while (buffer.chunks[i] !== undefined) {
        assembled.push(buffer.chunks[i]);
        i++;
      }
      const fullMessage = assembled.join('');

      buffer.latestCompleteMessage = fullMessage;

      const message =
        intelligenceAi?.done && !isResponseObject ? intelligenceAi?.response : fullMessage;
      const newMessageObj = {
        id: intelligenceAi?.intelligenceChatMessage?.id || v4(),
        message,
        isNote: intelligenceAi?.isNote,
        note: intelligenceAi?.note,
        emCPTsText: intelligenceAi?.emCPTsText,
        agent: 'ai'
      };

      setRecoil(activePromptConversationsState, (prevState = []) => {
        let newConversation = [...iaRa(prevState)];
        if (newConversation[newConversation?.length - 1]?.agent === 'ai') {
          newConversation[newConversation?.length - 1] = newMessageObj;
        } else {
          newConversation = [...iaRa(prevState), newMessageObj];
        }
        return newConversation;
      });

      if (isLast) {
        delete chunkBuffers[chatId];
        setRecoil(isAIGeneratingState, false);
      }
    }
  }
};

export default updateAutoMapTemplate;
